import React, { FormEvent, ChangeEvent } from "react";
import { ThemeProvider } from "@material-ui/core/styles";
import { TextField } from "@material-ui/core";
import { SubmitButton } from "../../../input/component/submit_button/SubmitButton";
import { ThemeUtil } from "../../../util/ThemeUtil";
import "./Admin2FAForm.scss";

interface Props {
    onSubmit: (code: string) => void;
    onResend?: () => void;
    resendCooldownSeconds?: number;
    resendLoading?: boolean;
    error?: string;
    loading: boolean;
}

interface State {
    code: string;
}

export class Admin2FAForm extends React.Component<Props, State> {
    private readonly inputTheme = ThemeUtil.usingGlobalTheme({});

    constructor(props: Props) {
        super(props);
        this.state = { code: "" };
    }

    private handleCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
        this.setState({ code: e.target.value.trim() });
    };

    private handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        if (this.props.loading) return;
        const { code } = this.state;
        if (!code) return;
        this.props.onSubmit(code);
    };

    private handleResend = () => {
        if (this.props.resendLoading || (this.props.resendCooldownSeconds ?? 0) > 0 || !this.props.onResend) return;
        this.props.onResend();
    };

    render() {
        const { error, loading, onResend, resendCooldownSeconds = 0, resendLoading = false } = this.props;
        const { code } = this.state;
        const canSubmit = Boolean(code && !loading);
        const canResend = Boolean(onResend && !resendLoading && resendCooldownSeconds <= 0);

        return (
            <div className="admin-2fa-form">
                <p className="admin-2fa-form-hint">
                    Enter the verification code sent to your phone.
                </p>
                <form onSubmit={this.handleSubmit}>
                    <ThemeProvider theme={this.inputTheme}>
                        <TextField
                            className="admin-2fa-form-field"
                            label="Verification code"
                            type="text"
                            inputProps={{ inputMode: "numeric", maxLength: 8 }}
                            value={code}
                            onChange={this.handleCodeChange}
                            error={Boolean(error)}
                            required
                            fullWidth
                            autoComplete="one-time-code"
                            disabled={loading}
                            placeholder="e.g. 123456"
                        />
                    </ThemeProvider>
                    {error && (
                        <p className="admin-2fa-form-error" role="alert">
                            {error}
                        </p>
                    )}
                    <div className="admin-2fa-form-actions">
                        <SubmitButton type="submit" disabled={!canSubmit}>
                            {loading ? "Verifying…" : "Verify"}
                        </SubmitButton>
                        {onResend && (
                            <div className="admin-2fa-form-resend">
                                <button
                                    type="button"
                                    className="admin-2fa-form-resend-btn"
                                    onClick={this.handleResend}
                                    disabled={!canResend}
                                >
                                    {resendLoading
                                        ? "Sending…"
                                        : resendCooldownSeconds > 0
                                            ? `Resend in ${resendCooldownSeconds}s`
                                            : "Resend code"}
                                </button>
                            </div>
                        )}
                    </div>
                </form>
            </div>
        );
    }
}
